/**
 * <s-build-your-own> — Build-your-own bundle picker. Shoppers add items (any
 * option, any mix) from a collection until the bundle size is reached, then add
 * the whole bundle in one click. Every line carries the same visible "Bundle"
 * property listing each pick, plus a hidden _bundle_id, so the cart, checkout
 * and order show the items were chosen together. Any saving shown must match
 * an automatic discount on the collection (for example a Hoppy quantity break).
 *
 * Commands (buttons use commandfor="<this id>"):
 *   --more       — Adds one of the source button's item (its selected option).
 *   --less       — Removes the last pick of the source button's item.
 *   --add-bundle — Adds every pick to the cart, then opens the cart.
 *
 * Required child markup:
 *   [data-byob-item][data-title] > [data-byob-variant] (select with option[data-price][data-label], or input[data-price][data-label])
 *   [data-byob-item] [data-byob-count] — Per-item pick count.
 *   [data-byob-progress] [data-byob-list] [data-byob-total] [data-byob-original] [data-byob-savings]
 *   [data-byob-submit] [data-byob-error]
 *
 * Element dataset:
 *   data-size, data-percent, data-money-format, data-property-key, data-bundle-title,
 *   data-progress-label (__chosen__), data-remaining-label (__count__), data-add-label,
 *   data-savings-label ({{ amount }}), data-full-message, data-error-message,
 *   data-cart-add-url / data-cart-url — Liquid routes for the no-Standard-Actions fallback.
 */
class SBuildYourOwn extends HTMLElement {
  #ac;
  #picks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      const item = event.source?.closest('[data-byob-item]');
      if (event.command === '--more' && item) this.#more(item);
      if (event.command === '--less' && item) this.#less(item);
      if (event.command === '--add-bundle') this.#submit(event.source);
    }, { signal: this.#ac.signal });
    this.addEventListener('change', (event) => {
      const item = event.target?.closest?.('[data-byob-item]');
      if (item) this.#syncItemPrice(item);
    }, { signal: this.#ac.signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #size() {
    return Math.max(Number.parseInt(this.dataset.size || '3', 10) || 3, 1);
  }

  #option(item) {
    const variant = item.querySelector('[data-byob-variant]');
    return variant instanceof HTMLSelectElement ? variant.selectedOptions[0] : variant;
  }

  #syncItemPrice(item) {
    const option = this.#option(item);
    const price = item.querySelector('[data-byob-price] bdi');
    if (price && option?.dataset.price) price.textContent = this.#money(Number(option.dataset.price));
  }

  #more(item) {
    if (this.#picks.length >= this.#size) {
      this.#showError(this.dataset.fullMessage || '');
      return;
    }
    const option = this.#option(item);
    if (!option?.value) return;
    const label = option.dataset.label || '';
    this.#picks.push({
      item,
      id: String(option.value),
      title: item.dataset.title || '',
      label: label && label !== 'Default Title' ? label : '',
      price: Number(option.dataset.price || 0),
    });
    this.#showError('');
    this.#render();
  }

  #less(item) {
    for (let index = this.#picks.length - 1; index >= 0; index -= 1) {
      if (this.#picks[index].item === item) {
        this.#picks.splice(index, 1);
        break;
      }
    }
    this.#showError('');
    this.#render();
  }

  // Groups picks into { id, name, quantity } in the order they were first chosen.
  #grouped() {
    const grouped = new Map();
    for (const pick of this.#picks) {
      const entry = grouped.get(pick.id) || { id: pick.id, name: pick.label ? `${pick.title} (${pick.label})` : pick.title, quantity: 0 };
      entry.quantity += 1;
      grouped.set(pick.id, entry);
    }
    return [...grouped.values()];
  }

  #render() {
    const size = this.#size;
    const chosen = this.#picks.length;
    const complete = chosen === size;

    for (const item of this.querySelectorAll('[data-byob-item]')) {
      const count = this.#picks.filter((pick) => pick.item === item).length;
      const countEl = item.querySelector('[data-byob-count]');
      if (countEl) countEl.textContent = String(count);
      item.classList.toggle('ring-2', count > 0);
      item.classList.toggle('ring-accent', count > 0);
      const less = item.querySelector('[command="--less"]');
      if (less) less.disabled = count === 0;
      const more = item.querySelector('[command="--more"]');
      if (more) more.disabled = complete;
    }

    const progress = this.querySelector('[data-byob-progress]');
    if (progress) progress.textContent = (this.dataset.progressLabel || '').replace('__chosen__', String(chosen));

    const list = this.querySelector('[data-byob-list]');
    if (list) {
      list.replaceChildren(...this.#grouped().map((entry) => {
        const li = document.createElement('li');
        li.className = 'flex justify-between gap-3 py-1 text-sm text-on-surface';
        const name = document.createElement('span');
        name.className = 'min-w-0';
        name.textContent = entry.name;
        const qty = document.createElement('span');
        qty.className = 'shrink-0 text-on-surface-secondary';
        qty.textContent = `× ${entry.quantity}`;
        li.append(name, qty);
        return li;
      }));
    }

    const subtotal = this.#picks.reduce((sum, pick) => sum + pick.price, 0);
    const percent = Math.min(Math.max(Number(this.dataset.percent || 0), 0), 100);
    const discount = complete && percent > 0 ? Math.round(subtotal * (percent / 100)) : 0;
    const total = subtotal - discount;

    const totalEl = this.querySelector('[data-byob-total] bdi');
    if (totalEl) totalEl.textContent = this.#money(total);
    const original = this.querySelector('[data-byob-original]');
    if (original) {
      original.hidden = discount <= 0;
      const bdi = original.querySelector('bdi');
      if (bdi) bdi.textContent = discount > 0 ? this.#money(subtotal) : '';
    }
    const savings = this.querySelector('[data-byob-savings]');
    if (savings) {
      savings.hidden = discount <= 0;
      savings.textContent = discount > 0 ? (this.dataset.savingsLabel || '').replace(/\{\{\s*amount\s*\}\}/g, this.#money(discount)) : '';
    }

    const submit = this.querySelector('[data-byob-submit]');
    if (submit) {
      submit.disabled = !complete;
      submit.textContent = complete
        ? (this.dataset.addLabel || '')
        : (this.dataset.remainingLabel || '').replace('__count__', String(size - chosen));
    }
  }

  async #submit(button) {
    if (this.getAttribute('aria-busy') === 'true' || this.#picks.length !== this.#size) return;

    const grouped = this.#grouped();
    const summary = grouped.map((entry) => `${entry.name} ×${entry.quantity}`).join(', ');
    const propertyKey = this.dataset.propertyKey || 'Bundle';
    const propertyValue = `${this.dataset.bundleTitle || ''} — ${summary}`;
    const bundleId = Math.random().toString(36).slice(2, 8).toUpperCase();

    this.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    this.#showError('');

    try {
      if (window.Shopify?.actions?.updateCart) {
        const lines = grouped.map((entry) => ({
          merchandiseId: entry.id,
          quantity: entry.quantity,
          attributes: [{ key: propertyKey, value: propertyValue }, { key: '_bundle_id', value: bundleId }],
        }));
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-build-your-own' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        this.#picks = [];
        this.#render();
        await window.Shopify.actions.openCart?.();
      } else {
        // cart/add.js returns JSON; the route base comes from Liquid via data-cart-add-url.
        const items = grouped.map((entry) => ({
          id: Number(entry.id),
          quantity: entry.quantity,
          properties: { [propertyKey]: propertyValue, _bundle_id: bundleId },
        }));
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.description || '');
        }
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      this.#showError(error?.message || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-byob-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-build-your-own')) {
  customElements.define('s-build-your-own', SBuildYourOwn);
}
